import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import HiredLogo from "@/components/brand/HiredLogo";
import { ArrowRight, Loader2 } from "lucide-react";

const STEPS = ["Welcome", "Business", "Ready"];

export default function Onboarding() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [ce, setCe] = useState(null);
  const [profile, setProfile] = useState({ full_name: "", business_name: "", industry: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: emps }, { data: biz }] = await Promise.all([
          api.get("/customer-employees"),
          api.get("/business"),
        ]);
        if (emps.length) setCe(emps[0]);
        setProfile({
          full_name: user?.full_name || "",
          business_name: (biz?.name || "").startsWith((user?.email || "").split("@")[0]) ? "" : (biz?.name || ""),
          industry: biz?.industry || "",
        });
      } catch (e) {
        toast.error("Failed to load onboarding");
      }
    })();
  }, [user]);

  const saveStep1 = async (e) => {
    e.preventDefault();
    if (!profile.full_name.trim()) { toast.error("Enter your name"); return; }
    setBusy(true);
    try {
      await api.patch("/auth/profile", { full_name: profile.full_name });
      await refresh();
      setStep(2);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const saveStep2 = async (e) => {
    e.preventDefault();
    if (!profile.business_name.trim()) { toast.error("Enter your business name"); return; }
    setBusy(true);
    try {
      await api.patch("/auth/profile", {
        business_name: profile.business_name,
        industry: profile.industry || null,
      });
      setStep(3);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    try { await api.post("/onboarding/complete"); } catch {}
    await refresh();
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <header className="border-b border-line px-6 md:px-10 py-5 flex items-center justify-between">
        <HiredLogo size={20} />
        <div className="text-steel text-xs tracking-widest uppercase">Onboarding · Step {step} of {STEPS.length}</div>
      </header>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <div className="grid grid-cols-3 gap-2 mb-14">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 ${i < step ? "bg-gold" : "bg-line2"}`} />
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={saveStep1} data-testid="ob-step-1">
            <div className="brand-eyebrow mb-6">STEP 01</div>
            <h1 className="brand-headline text-5xl md:text-6xl">Welcome to <span className="text-purple italic">Hired!</span></h1>
            <p className="text-steel text-lg mt-6 max-w-xl">Let&apos;s get you set up. First, what should we call you?</p>
            <div className="mt-10 max-w-md">
              <label htmlFor="ob-name">Full name</label>
              <input data-testid="ob-name" id="ob-name" required placeholder="Alex Rivera"
                     value={profile.full_name} onChange={e => setProfile({...profile, full_name: e.target.value})} />
            </div>
            <button data-testid="ob-next-1" type="submit" disabled={busy} className="btn-gold mt-10">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <>Continue <ArrowRight size={16} /></>}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={saveStep2} data-testid="ob-step-2">
            <div className="brand-eyebrow mb-6">STEP 02</div>
            <h1 className="brand-headline text-5xl md:text-6xl">Tell us about <span className="text-purple italic">your business.</span></h1>
            <p className="text-steel text-lg mt-6 max-w-xl">Two quick fields. You can change them anytime in Settings.</p>
            <div className="mt-10 space-y-5 max-w-md">
              <div>
                <label htmlFor="ob-business">Business name</label>
                <input data-testid="ob-business-name" id="ob-business" required placeholder="Acme HOA"
                       value={profile.business_name} onChange={e => setProfile({...profile, business_name: e.target.value})} />
              </div>
              <div>
                <label htmlFor="ob-industry">Industry (optional)</label>
                <input data-testid="ob-industry" id="ob-industry" placeholder="Community Management"
                       value={profile.industry} onChange={e => setProfile({...profile, industry: e.target.value})} />
              </div>
            </div>
            <div className="mt-10 flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn-outline">Back</button>
              <button data-testid="ob-next-2" type="submit" disabled={busy} className="btn-gold">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Continue <ArrowRight size={16} /></>}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div data-testid="ob-step-3">
            <div className="brand-eyebrow mb-6">STEP 03 · READY</div>
            <h1 className="brand-headline text-5xl md:text-6xl">Your AI employee is <span className="text-purple italic">ready.</span></h1>
            <p className="text-steel text-lg mt-6 max-w-xl">Everything&apos;s set up. Meet {ce?.display_name || "your new employee"}.</p>
            <div className="card-white p-10 mt-10">
              <div className="brand-eyebrow" style={{color:'#0B0D10'}}>YOUR TEAM</div>
              <div className="brand-headline text-4xl mt-3 text-obsidian">{ce?.display_name || "AI Employee"}</div>
              <div className="text-obsidian/70 text-sm mt-2">{ce?.ai_employee_slug === "hoa-steward" ? "HOA Community Manager" : ce?.ai_employee_slug === "homihq" ? "Real Estate Operations Manager" : ce?.ai_employee_slug === "foundation-first" ? "Construction Operations Manager" : "Ready to work"}</div>
              <div className="mt-6 flex items-center gap-3 text-sm text-obsidian">
                <span className="dot dot-green" /> Trial active · 14 days remaining
              </div>
            </div>
            <button data-testid="ob-finish" onClick={finish} className="btn-gold mt-10">
              Go to Dashboard <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
